import React, { useEffect, useRef, useState } from "react";
import useFleetList from "./hooks/useFleetList";
import ListHeader from "./components/list-header";
import ListFilters from "./components/list-filters";
import ListTable from "./components/list-table";
import ListModals from "./components/list-modals";
export { fleetPageRouteName } from "../constants/routes";

const FleetPage = () => {
  const {
    columns,
    currentPage,
    deleteRow,
    paginatedRows,
    rowsPerPage,
    searchValue,
    selectedKeys,
    setCurrentPage,
    setRowsPerPage,
    setSearchValue,
    setSelectedKeys,
    setShowDeleteConfirmModal,
    setSortState,
    setStatusFilter,
    setVehicleTypeFilter,
    setVehicleModelFilter,
    showDeleteConfirmModal,
    sortState,
    statusFilter,
    vehicleTypeFilter,
    vehicleModelFilter,
    t,
    isLoading,
    isFetching,
    totalPages,
    totalSelected,
    draftCount,
    showImportStatusModal,
    setShowImportStatusModal,
    importStatusTitle,
    importStatusMessage,
    importStatusType,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    bulkActionStatus,
    onConfirmBulkStatusUpdate,
    onAddFleet,
    onConfirmDelete,
    onDraft,
    onExport,
    onImport,
    isFilterActive,
    onClearFilter,
    vehicleTypeOptions,
    vehicleModelOptions,
    isImportOpen,
    isImportLoading,
    selectedFile,
    onCloseImport,
    onFileSelect,
    onFileRemove,
    onTemplateDownload,
    onSubmitImport,
    isExportOpen,
    isExportLoading,
    onExportClick,
    onCloseExport,
    onExportFormatChange,
    onExportTypeChange,
    exportFormat,
    exportType,
    allowedBulkActions,
    onBulkStatusUpdate,
    selectedPlateNumbers,
    modalTargetKeys,
    modalTargetPlateNumbers,
    isBulkDisabled,
    isBulkUpdating,
    onRowClick,
  } = useFleetList();

  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [stagedBulkAction, setStagedBulkAction] = useState<string | null>(null);
  const bulkRef = useRef<HTMLDivElement>(null);

  const effectiveStagedBulkAction = totalSelected === 0 ? null : stagedBulkAction;

  useEffect(() => {
    if (!isBulkOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (bulkRef.current && !bulkRef.current.contains(e.target as Node)) {
        setIsBulkOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isBulkOpen]);

  const getBulkActionLabel = (action: string | null) => {
    if (!action) return t("btn-bulk-actions");
    if (action === "Inactive") return t("bulk-action-deactivate");
    if (action === "Available") return t("bulk-action-activate");
    if (action === "Maintenance") return t("bulk-action-maintenance");
    if (action === "Delete") return t("bulk-action-delete");
    return action;
  };

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        t={t}
        onDraft={onDraft}
        onExportClick={onExportClick}
        onImport={onImport}
        onAddFleet={onAddFleet}
        totalSelected={totalSelected}
        isBulkOpen={isBulkOpen}
        setIsBulkOpen={setIsBulkOpen}
        isBulkDisabled={isBulkDisabled}
        bulkRef={bulkRef}
        allowedBulkActions={allowedBulkActions}
        stagedBulkAction={effectiveStagedBulkAction}
        setStagedBulkAction={setStagedBulkAction}
        onBulkStatusUpdate={onBulkStatusUpdate}
        getBulkActionLabel={getBulkActionLabel}
        draftCount={draftCount}
      />

      <div className="flex flex-col gap-4 relative z-30">
        <ListFilters
          t={t}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setCurrentPage={setCurrentPage}
          vehicleTypeFilter={vehicleTypeFilter}
          setVehicleTypeFilter={setVehicleTypeFilter}
          vehicleTypeOptions={vehicleTypeOptions}
          vehicleModelFilter={vehicleModelFilter}
          setVehicleModelFilter={setVehicleModelFilter}
          vehicleModelOptions={vehicleModelOptions}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          isFilterActive={isFilterActive}
          onClearFilter={onClearFilter}
        />
      </div>

      <ListTable
        t={t}
        paginatedRows={paginatedRows}
        isLoading={isLoading}
        isFetching={isFetching}
        columns={columns}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        setCurrentPage={setCurrentPage}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        sortState={sortState}
        setSortState={setSortState}
        totalPages={totalPages}
        onRowClick={onRowClick}
      />

      <ListModals
        t={t}
        showDeleteConfirmModal={showDeleteConfirmModal}
        setShowDeleteConfirmModal={setShowDeleteConfirmModal}
        deleteRow={deleteRow}
        onConfirmDelete={onConfirmDelete}
        showBulkConfirmModal={showBulkConfirmModal}
        setShowBulkConfirmModal={setShowBulkConfirmModal}
        bulkActionStatus={bulkActionStatus}
        selectedKeys={modalTargetKeys}
        selectedPlateNumbers={modalTargetPlateNumbers}
        onConfirmBulkStatusUpdate={onConfirmBulkStatusUpdate}
        isImportOpen={isImportOpen}
        onCloseImport={onCloseImport}
        onSubmitImport={onSubmitImport}
        onTemplateDownload={onTemplateDownload}
        onFileSelect={onFileSelect}
        onFileRemove={onFileRemove}
        selectedFile={selectedFile}
        isImportLoading={isImportLoading}
        showImportStatusModal={showImportStatusModal}
        setShowImportStatusModal={setShowImportStatusModal}
        importStatusType={importStatusType}
        importStatusTitle={importStatusTitle}
        importStatusMessage={importStatusMessage}
        isExportOpen={isExportOpen}
        onCloseExport={onCloseExport}
        onExport={onExport}
        exportFormat={exportFormat}
        exportType={exportType}
        onExportFormatChange={onExportFormatChange}
        onExportTypeChange={onExportTypeChange}
        isExportLoading={isExportLoading}
        isBulkUpdating={isBulkUpdating}
      />
    </div>
  );
};

export default FleetPage;
